import { createContext, useContext, useMemo } from 'react';
import { PanelDefinition } from '@/core';
import { usePanelKey } from '@/plugin-system/components/VizPanelContextProvider';
import { useRegisterPanelToGroup } from '@/plugin-system/runtime/VizPanelGroupProvider';

// NOTE: this context is separate from other `VizContainer` context because this context may change very often and
// we want to minimize unnecessary performance impact by keeping it separate.

interface VizContainerPanelDefinitionContextValue {
  /**
   * The dashboard panel definition for the chart. Used under the hood for add to dashboard.
   */
  panelDefinition?: PanelDefinition;
}

const DEFAULT_VIZ_CONTAINER_PANEL_DEFINITION_CONTEXT_VALUE: VizContainerPanelDefinitionContextValue = {
  panelDefinition: undefined,
};

const VizContainerPanelDefinitionContext = createContext<VizContainerPanelDefinitionContextValue>(
  // Intentionally defaulting the context with an undefined `panelDefinition` so that calls to get
  // the context never fail on undefined context. This makes it viable for us to slowly migrate some downstream
  // components from the old `VizPanel` approach of passing the panel definition down as a prop using `cloneElement`
  // to this new approach of passing it down as context without things erroring out in the old case.
  DEFAULT_VIZ_CONTAINER_PANEL_DEFINITION_CONTEXT_VALUE
);

export interface VizContainerPanelDefinitionContextProviderProps extends VizContainerPanelDefinitionContextValue {
  children: React.ReactNode;
}

export function VizContainerPanelDefinitionContextProvider({
  children,
  panelDefinition,
}: VizContainerPanelDefinitionContextProviderProps) {
  const panelKey = usePanelKey();

  const value = useMemo(() => {
    return { panelDefinition };
  }, [panelDefinition]);

  // Register the panel to the nearest VizPanelGroupProvider if it exists. This should be safe do generally because this
  // hook noops if the `panelDefinition` is undefined.
  useRegisterPanelToGroup(panelKey, panelDefinition as PanelDefinition);

  return (
    <VizContainerPanelDefinitionContext.Provider value={value}>{children}</VizContainerPanelDefinitionContext.Provider>
  );
}

export const useVizContainerPanelDefinitionContext = () => {
  return useContext(VizContainerPanelDefinitionContext);
};
